import type {
  ByfunnFrapiArticle,
  ByfunnPromoArticle,
} from '../types/frapi-types.js';
import type {
  PageType,
  TrackPageView,
} from '../../core/react/pulse/types/index.js';
import type { InsightReferrer } from '../views/components/articles/articleComponents/Salesposter/utils.js';

import {
  accessLevel,
  accessLevelName,
} from '../views/components/articles/utils/access.js';
import { getAuthorEmails } from '../views/components/articles/utils/authors.js';

const generateArticleObject = (
  article: ByfunnFrapiArticle | ByfunnPromoArticle,
) => {
  const imageUrl = article.mainImage
    ? `https://premium.vgc.no/ap/images/${article.mainImage.id}?fit=crop&h=878&q=80&tight=true&w=2048`
    : 'https://cdn.aftenposten.no/vink/images/wordmark.png';

  return {
    id: article.id,
    type: (isOpen(article) ? 'Article' : 'SalesPoster') as PageType,
    name: article.title,
    accessLevel: isOpen(article) ? accessLevel(article) : 'Paid',
    accessLevelName: isOpen(article) ? accessLevelName(article) : 'Paid',
    category: article.section,
    tags: article.tags.map((t) => t.title),
    contentId: article.id,
    'spt:custom': {
      ...(isOpen(article) && { 'spt:wordCount': article.wordCount }),
      ...(isOpen(article) && {
        'spt:authors': getAuthorEmails(article),
      }),
      'spt:published': article.published,
      'spt:update': article.updated,
      'spt:imageUrl': article?.mainImage && imageUrl,
    },
  };
};
const trackArticlePageView = (
  trackPageView: TrackPageView,
  article: ByfunnFrapiArticle,
) => {
  trackPageView({
    object: generateArticleObject(article),
  }).then((data) => data);
};

const trackFrontpageView = (trackPageView: TrackPageView) => {
  trackPageView({
    object: {
      id: 'frontpage',
      type: 'Listing',
      name: 'Frontpage - Vink',
    },
  }).then((data) => data);
};

const trackSectionPageView = (
  trackPageView: TrackPageView,
  sectionName: string,
) => {
  trackPageView({
    object: {
      id: 'section-page',
      type: 'Listing',
      name: sectionName,
    },
  }).then((data) => data);
};

const trackStaticPageView = (
  trackPageView: TrackPageView,
  pageType: PageType,
  componentName: string,
  custom?: Record<string, unknown>,
) => {
  trackPageView({
    object: {
      id: componentName === 'error' ? 'error:404' : componentName,
      type: pageType,
      name: `${pageType} - ${componentName}`,
      'spt:custom': {
        ...custom,
      },
    },
  }).then((data) => data);
};

const trackSearchPageView = (
  trackPageView: TrackPageView,
  currentUrl: string,
) => {
  const query = currentUrl.match('phrase=(.*)&')?.[1] || currentUrl;

  trackPageView({
    object: {
      id: 'search',
      type: 'Listing',
      name: 'Search Result Page',
      'spt:custom': {
        query,
      },
    },
  }).then((data) => data);
};

const trackTagPageView = (
  trackPageView: TrackPageView,
  currentUrl: string,
  tagName: string,
) => {
  trackPageView({
    object: {
      id: 'tag',
      type: 'Listing',
      name: tagName,
      'spt:custom': {
        currentUrl,
      },
    },
  }).then((data) => data);
};

const trackStoryPageView = (
  trackPageView: TrackPageView,
  currentUrl: string,
  storyName: string,
) => {
  trackPageView({
    object: {
      id: 'story',
      type: 'Listing',
      name: storyName,
      'spt:custom': {
        currentUrl,
      },
    },
  }).then((data) => data);
};

const trackSalesposterView = (
  trackPageView: TrackPageView,
  article: ByfunnPromoArticle,
  insightReferrer: InsightReferrer,
) => {
  trackPageView({
    object: {
      id: article.id,
      type: 'SalesPoster',
      name: article.title,
      accessLevel: 'Paid',
      accessLevelName: 'Paid',
      category: article.section,
      tags: article.tags.map((t) => t.title),
      contentId: article.id,
      'spt:custom': {
        'spt:published': article.published,
        'spt:update': article.updated,
        salesposterType: 'Paid content',
        referrerSource: insightReferrer.source,
        referrerContent: insightReferrer.content,
        referrerCampaign: insightReferrer.campaign,
        referrerMedium: insightReferrer.medium,
        referrerTag: insightReferrer.tag,
      },
    },
  }).then((data) => data);
};

const isOpen = (
  value: ByfunnFrapiArticle | ByfunnPromoArticle,
): value is ByfunnFrapiArticle =>
  Object.prototype.hasOwnProperty.call(value, 'wordCount');

export {
  trackArticlePageView,
  trackFrontpageView,
  trackSectionPageView,
  trackStaticPageView,
  trackSearchPageView,
  trackTagPageView,
  trackStoryPageView,
  trackSalesposterView,
};
